/**
 * Finance upload pipeline — preview-then-commit.
 *
 *   parseFinanceFile(file)            → /api/finance/parse  (PREVIEW, no writes)
 *   commitFinanceFile(file, code)     → /api/finance/commit (PERMANENT write)
 *
 * The preview endpoint returns the parsed descriptor, classifier output,
 * derived dates, and a `_dedup_hit` flag if the file's SHA-256 already exists
 * in finance_ingestion_sources. NO substrate write, NO storage upload, NO
 * legacy finance_uploads write.
 *
 * The commit endpoint requires `confirmation_code` (currently '1337') and
 * runs the substrate ingestion via persistSourceToSubstrate. Hash dedup
 * server-side keeps repeat commits idempotent.
 */

import { apiWrite } from './api-client.js';

const API_BASE = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8787'
  : 'https://pwd-api.adam-hoemberg.workers.dev';

export type FinanceDetectedType =
  | 'gf_statement'
  | 'service_worksheet'
  | 'daily_service_worksheet'
  | 'settlement'
  | 'payroll_drivers'
  | 'payroll_managers'
  | 'payroll_marion'
  | 'ispa_attachment_c1'
  | 'pdf_document'
  | null;

export interface IspaMetadata {
  ispa_number: string | null;
  ispa_version: string | null;
  effective_date: string | null;
  end_date: string | null;
  signed_date: string | null;
  signed_time: string | null;
  signers: Array<{ id: string; name: string; party: 'PWD' | 'FEC' | string }>;
}

export interface PdfMeta {
  is_pdf: boolean;
  encrypted: boolean;
  extraction_ok: boolean;
  extraction_reason: string | null;
  text_length: number;
  stream_count: number;
}

export interface DedupHit {
  existing_source_id: string;
  original_filename?: string;
  uploaded_at?: string;
  ingestion_status?: string;
}

export interface ParsedFile {
  filename?: string;
  detected_type?: FinanceDetectedType;
  week_ending?: string;
  service_date?: string;
  period_start?: string;
  period_end?: string;
  summary?: Record<string, unknown>;
  flags?: Array<{ severity?: string; message?: string; [k: string]: unknown }>;
  raw_rows?: unknown[];
  raw_headers?: unknown[];
  raw_row_count?: number;
  crosswalk_rows?: unknown[];
  error?: string;
  _detect_trace?: string;
  _content_hash_sha256?: string;
  _dedup_hit?: DedupHit;
  _parse_error?: string;
  _pdf?: PdfMeta;
  _ispa_metadata?: IspaMetadata | null;
  [k: string]: unknown;
}

interface ParseResponse {
  files?: ParsedFile[];
  preview_only?: boolean;
  error?: string;
}

interface CommitResponseFile {
  filename?: string;
  ok?: boolean;
  source_id?: string;
  detected_type?: FinanceDetectedType;
  week_ending?: string;
  duplicate?: boolean;
  units_created?: number;
  rows_created?: number;
  cells_created?: number;
  error?: string;
}

interface CommitResponse {
  files?: CommitResponseFile[];
  all_ok?: boolean;
  error?: string;
}

/**
 * POST /api/finance/parse — preview, NO writes.
 */
export async function parseFinanceFile(file: File): Promise<{ ok: boolean; file?: ParsedFile; error?: string }> {
  const form = new FormData();
  form.append('file', file);

  const token = localStorage.getItem('pwd_manager_session') || '';
  let res: Response;
  try {
    res = await fetch(API_BASE + '/api/finance/parse', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
  } catch {
    return { ok: false, error: 'Connection error during preview.' };
  }

  let body: ParseResponse;
  try {
    body = (await res.json()) as ParseResponse;
  } catch {
    return { ok: false, error: 'Worker returned invalid JSON.' };
  }

  if (!res.ok || body.error) {
    return { ok: false, error: body.error || `Preview failed (HTTP ${res.status}).` };
  }

  const first = (body.files || [])[0];
  if (!first) return { ok: false, error: 'Parser returned no files.' };

  if (first._parse_error) {
    return { ok: false, error: 'Parse error: ' + first._parse_error };
  }

  return { ok: true, file: first };
}

/**
 * POST /api/finance/commit — PERMANENT write. Requires confirmation code.
 *
 * `pdfExtractedText` (optional) is the text the client pulled out of a PDF
 * with pdfjs. The worker uses it to populate metadata + text_blocks when its
 * own worker-side extraction is blocked by encryption.
 */
export async function commitFinanceFile(file: File, confirmationCode: string, pdfExtractedText?: string): Promise<{ ok: boolean; result?: CommitResponseFile; error?: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('confirmation_code', confirmationCode);
  if (pdfExtractedText && pdfExtractedText.length > 0) {
    form.append('pdf_extracted_text', pdfExtractedText);
  }

  const token = localStorage.getItem('pwd_manager_session') || '';
  let res: Response;
  try {
    res = await fetch(API_BASE + '/api/finance/commit', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
  } catch {
    return { ok: false, error: 'Connection error during commit.' };
  }

  let body: CommitResponse;
  try {
    body = (await res.json()) as CommitResponse;
  } catch {
    return { ok: false, error: 'Worker returned invalid JSON.' };
  }

  // 403 = wrong confirmation code; surface the message clearly.
  if (res.status === 403) {
    return { ok: false, error: body.error || 'Wrong confirmation code.' };
  }

  const first = (body.files || [])[0];
  if (!res.ok || (first && !first.ok)) {
    return { ok: false, error: (first && first.error) || body.error || `Commit failed (HTTP ${res.status}).`, result: first };
  }

  return { ok: true, result: first };
}

// ----------------------------------------------------------------------------
// Legacy stage/import endpoints — retained for any external caller that still
// uses the old 3-step flow. The V2 client uses commitFinanceFile() instead.
// ----------------------------------------------------------------------------

interface StageResponse { upload_id?: string | number; error?: string; [k: string]: unknown; }
interface ImportResponse { success?: boolean; imported?: number; error?: string; [k: string]: unknown; }

export async function stageFinanceFile(parsed: ParsedFile): Promise<{ ok: boolean; uploadId?: string | number; error?: string }> {
  const payload = {
    filename: parsed.filename,
    file_type: parsed.detected_type || undefined,
    week_ending: parsed.week_ending,
    parsed_data: parsed,
    flags: parsed.flags || [],
    raw_rows: parsed.raw_rows || [],
    raw_headers: parsed.raw_headers || [],
    crosswalk_rows: parsed.crosswalk_rows || [],
  };
  const { ok, data } = await apiWrite<StageResponse>('/api/finance/stage', 'POST', payload);
  if (!ok || data.error) return { ok: false, error: data.error || 'Stage failed.' };
  if (!data.upload_id) return { ok: false, error: 'Stage did not return an upload id.' };
  return { ok: true, uploadId: data.upload_id };
}

export async function importFinanceUpload(uploadId: string | number, confirmationCode: string): Promise<{ ok: boolean; imported?: number; error?: string }> {
  const { ok, data } = await apiWrite<ImportResponse>('/api/finance/import', 'POST', {
    upload_id: uploadId,
    confirmation_code: confirmationCode,
  });
  if (!ok || data.error) return { ok: false, error: data.error || 'Import failed.' };
  return { ok: true, imported: typeof data.imported === 'number' ? data.imported : undefined };
}
